import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { User, Agency } from '../../../../shared/types';
import { StorageManager } from '../../utils/storageManager';

interface AuthState {
  user: User | null;
  token: string | null;
  agency: Agency | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

// Helper function to sync auth data with localStorage
const syncAuthData = (token: string | null, user: any = null, agency: any = null) => {
  console.log('Syncing auth data with localStorage:', { 
    hasToken: !!token, 
    hasUser: !!user, 
    hasAgency: !!agency 
  });
  
  if (token) {
    StorageManager.setToken(token);
  } else {
    StorageManager.removeToken();
  }
  
  if (user) {
    localStorage.setItem('user', JSON.stringify(user));
  } else {
    localStorage.removeItem('user');
  }
  
  if (agency) {
    localStorage.setItem('agency', JSON.stringify(agency));
  } else {
    localStorage.removeItem('agency');
  }
};

// Get initial auth data from localStorage
const getInitialAuthData = () => {
  const token = StorageManager.getToken();
  const userStr = localStorage.getItem('user');
  const agencyStr = localStorage.getItem('agency');
  
  let user = null;
  let agency = null;
  
  // Only load user/agency if we have a token (otherwise clear stale data)
  if (token) {
    try {
      if (userStr) user = JSON.parse(userStr);
      if (agencyStr) agency = JSON.parse(agencyStr);
    } catch (error) {
      console.error('Error parsing stored auth data:', error);
      // Clear invalid data
      localStorage.removeItem('user');
      localStorage.removeItem('agency');
    }
  } else {
    // No token - clear any stale auth data
    if (userStr) localStorage.removeItem('user');
    if (agencyStr) localStorage.removeItem('agency');
  }
  
  console.log('Initial auth data from localStorage:', { 
    hasToken: !!token, 
    hasUser: !!user, 
    hasAgency: !!agency 
  });
  
  return { token, user, agency };
};

const { token, user, agency } = getInitialAuthData();

const initialState: AuthState = {
  user: user,
  token: token,
  agency: agency,
  isAuthenticated: !!token, // Set isAuthenticated based on token presence
  loading: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart: (state) => {
      console.log('Login start');
      state.loading = true;
      state.error = null;
    },
    loginSuccess: (state, action: PayloadAction<{ user: User; token: string; agency?: Agency }>) => {
      console.log('Login success with payload:', action.payload);
      state.loading = false;
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.token = action.payload.token;
      // Use spread to ensure all agency fields (including bannerImageUrl/bannerTitle) are included
      state.agency = action.payload.agency ? { ...action.payload.agency } : null;
      syncAuthData(action.payload.token, action.payload.user, action.payload.agency ? { ...action.payload.agency } : null);
      console.log('State after login success:', state);
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      console.log('Login failure:', action.payload);
      state.loading = false;
      state.error = action.payload;
      state.isAuthenticated = false;
      syncAuthData(null);
    },
    logout: (state) => {
      console.log('Logout action - Clearing auth state');
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.agency = null;
      state.loading = false;
      state.error = null;
      syncAuthData(null);
      console.log('Logout action - Auth state cleared');
    },
    updateUser: (state, action: PayloadAction<User>) => {
      console.log('Updating user:', action.payload);
      state.user = action.payload;
      // Sync updated user to localStorage
      syncAuthData(state.token, action.payload, state.agency);
    },
    setToken: (state, action: PayloadAction<string>) => {
      console.log('Setting token:', action.payload);
      state.token = action.payload;
      state.isAuthenticated = true;
      syncAuthData(action.payload);
    },
  },
});

export const { loginStart, loginSuccess, loginFailure, logout, updateUser, setToken } = authSlice.actions;
export default authSlice.reducer; 